import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLinkEmployeeUser, useUnlinkedUsers, useUnlinkEmployeeUser } from "../lib/api";
import { ErrorBox } from "./ui";

const COMBINING_DIACRITICS = new RegExp("[̀-ͯ]", "g");
const suggestEmail = (name: string) => {
  const ascii = name.normalize("NFKD").replace(COMBINING_DIACRITICS, "");
  const slug = ascii.toLowerCase().replace(/[^a-z0-9]+/g, ".").replace(/^\.+|\.+$/g, "");
  return slug ? `${slug}@onesphere.local` : "";
};

type Props = { employee: string; employeeName: string; userId: string | null };

/** Employee ↔ User account link (Administration, People): pick an existing unlinked account or create one, so the
 * employee can sign in and see their own dashboard. Shared between the Employee list and Administration. */
export function EmployeeAccountLink({ employee, employeeName, userId }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"existing" | "create">("create");
  const [pickedUser, setPickedUser] = useState("");
  const [email, setEmail] = useState(() => suggestEmail(employeeName));
  const unlinked = useUnlinkedUsers(open && mode === "existing" ? "" : undefined);
  const link = useLinkEmployeeUser();
  const unlink = useUnlinkEmployeeUser();

  if (userId) {
    return (
      <div className="flex items-center gap-2 text-xs" data-employee-account={employee}>
        <span className="rounded border border-status-green/40 bg-green-50 px-2 py-0.5 font-semibold text-status-green">{userId}</span>
        <button onClick={() => unlink.mutate(employee)} disabled={unlink.isPending}
          className="text-brand-grey underline decoration-dotted hover:text-status-red disabled:opacity-50" data-employee-unlink>
          {t("ppl.account.unlink")}
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="rounded border border-brand-primary px-2 py-0.5 text-xs font-semibold text-brand-primary hover:bg-blue-50" data-employee-link-open={employee}>
        {t("ppl.account.link")}
      </button>
    );
  }

  return (
    <div className="w-64 rounded border border-brand-grey-light bg-white p-2 text-xs shadow-sm" data-employee-link-form={employee}>
      <div className="mb-1 flex gap-1">
        <button onClick={() => setMode("create")} className={`flex-1 rounded px-2 py-1 font-semibold ${mode === "create" ? "bg-brand-primary text-white" : "bg-brand-grey-light text-brand-charcoal"}`}>
          {t("ppl.account.create")}
        </button>
        <button onClick={() => setMode("existing")} className={`flex-1 rounded px-2 py-1 font-semibold ${mode === "existing" ? "bg-brand-primary text-white" : "bg-brand-grey-light text-brand-charcoal"}`}>
          {t("ppl.account.existing")}
        </button>
      </div>

      {mode === "create" ? (
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@onesphere.local"
          className="w-full rounded border border-brand-grey-light px-2 py-1" data-employee-link-email />
      ) : (
        <select value={pickedUser} onChange={(e) => setPickedUser(e.target.value)} className="w-full rounded border border-brand-grey-light px-2 py-1" data-employee-link-select>
          <option value="">{unlinked.isLoading ? t("common.loading") : t("ppl.account.choose")}</option>
          {(unlinked.data ?? []).map((u) => <option key={u.name} value={u.name}>{u.full_name} — {u.name}</option>)}
        </select>
      )}

      {link.isError && <div className="mt-1"><ErrorBox error={link.error} /></div>}

      <div className="mt-2 flex justify-end gap-1">
        <button onClick={() => setOpen(false)} className="rounded px-2 py-1 text-brand-grey hover:text-brand-charcoal">{t("common.cancel")}</button>
        <button
          disabled={link.isPending || (mode === "create" ? !email.includes("@") : !pickedUser)}
          onClick={() =>
            link.mutate(
              mode === "create" ? { employee, mode, email } : { employee, mode, user: pickedUser },
              { onSuccess: () => setOpen(false) },
            )
          }
          className="rounded bg-brand-primary px-2 py-1 font-semibold text-white disabled:opacity-50" data-employee-link-submit>
          {link.isPending ? t("common.saving") : t("ppl.account.link")}
        </button>
      </div>
    </div>
  );
}
